import './navigation.scss';

/**
 * Controller for the global site navigation (site header, burger, sub/third nav).
 *
 * Responsibilities:
 * - Manage burger menu open/close incl. focus trap and body scroll lock
 * - Handle sub navigation and third level navigation show/hide with animations
 * - Keyboard accessibility (Arrow navigation, Home/End, Escape)
 * - Keep ARIA attributes in sync (aria-expanded on triggers, aria-hidden on panels)
 * - Close/open behavior across responsive breakpoints
 *
 * Public API: const api = new Navigation($root)
 */
class Navigation {
    /**
     * @param {HTMLElement} element Root element of the site header/navigation.
     */
    constructor (element) {
        /** @type {HTMLElement} */
        this.$root = element;

        /** @type {HTMLElement|null} */
        this.$burger = this.$root.querySelector('[data-nav="burger"]');
        /** @type {HTMLElement|null} */
        this.$body = document.querySelector('body');

        /** @type {(e: KeyboardEvent) => void | null} */
        this._focusTrapHandler = /** @type {any} */(null);

        this.initialize();
    }

    /**
     * Initialize navigation: bind events and responsive behavior.
     * @returns {void}
     */
    initialize () {
        if (!this.$root) return;
        this.setEvents();
        this.resizeNavigation();
    }

    /**
     * Register a responsive hook to auto-close the burger on desktop widths.
     * @returns {void}
     */
    resizeNavigation () {
        let resizeTimer;
        window.addEventListener('resize', () => {
            this.$root.classList.add('is--resizing');
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                this.$root.classList.remove('is--resizing');
            }, 200);
        });

        // Defensive: only register if the custom resize handler exists
        window.resizeHandler?.customFunctions?.push?.(() => {
            if (window.innerWidth > 1023 && this.$burger?.classList.contains('is--open')) {
                this.$burger.click();
            }
        });
    }

    /**
     * Bind all required DOM events for navigation behavior.
     * @returns {void}
     */
    setEvents () {
        if (this.$burger) {
            this.$burger.addEventListener('click', () => {
                this.$burger?.classList.add('has--noevent');

                if (this.$burger?.classList.contains('is--open')) {
                    // Close burger
                    this.$burger.classList.remove('is--open');
                    this.$burger.setAttribute('aria-expanded', 'false');
                    this.disableFocusTrap();
                    if (this.$root.classList.contains('has--sub-nav-open')) {
                        const $subnav = document.querySelector('[data-nav="sub-nav"].is--active');
                        $subnav?.classList.remove('is--active');
                        this.$body?.classList.remove('no-scroll');
                        setTimeout(() => {
                            this.$root.classList.remove('has--mobile-menu-visible');
                            this.$root.classList.remove('has--sub-nav-open');
                            this.$root.classList.add('no--first-level-animation');
                        }, 500);
                        setTimeout(() => {
                            this.$root.classList.remove('no--first-level-animation');
                            this.$root.classList.remove('has--animated-header');
                            $subnav?.classList.remove('is--hiding');
                            // update aria states
                            const $activeTrigger = this.$root.querySelector('[data-nav="item"][aria-expanded="true"]');
                            if ($activeTrigger) $activeTrigger.setAttribute('aria-expanded', 'false');
                            if ($subnav) $subnav.setAttribute('aria-hidden', 'true');
                        }, 1000);
                    } else {
                        this.$root.classList.remove('has--mobile-menu-visible');
                        this.$root.classList.add('is--hiding');
                        this.$root.classList.add('is--hiding-opacity');
                        this.$body?.classList.remove('no-scroll');
                        setTimeout(() => {
                            this.$root.classList.remove('is--hiding-opacity');
                        }, 1100);
                        setTimeout(() => {
                            this.$root.classList.remove('is--hiding');
                            this.$root.classList.remove('has--animated-header');
                        }, 1100);
                    }
                } else {
                    // Open burger
                    this.$burger?.classList.add('is--open');
                    this.$burger?.setAttribute('aria-expanded', 'true');
                    this.$root.classList.add('has--mobile-menu-visible');
                    this.$body?.classList.add('no-scroll');
                    this.enableFocusTrap();
                    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
                    if (scrollTop > 20) {
                        this.$root.classList.add('has--animated-header');
                    }
                }

                setTimeout(() => {
                    this.$burger?.classList.remove('has--noevent');
                }, 1100);
            });

            // Close with Escape when burger menu is open
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && this.$burger?.classList.contains('is--open')) {
                    this.$burger.click();
                }
            });
        }

        const $menuItems = this.$root.querySelectorAll('[data-nav="item"]');
        if ($menuItems.length > 0) {
            for (let i = 0; i < $menuItems.length; i++) {
                const $menuItem = $menuItems[i];

                // mouse click handler
                $menuItem.addEventListener('click', (e) => {
                    const $parentLi = $menuItem.closest('li');
                    const hasChildren = $parentLi && $parentLi.classList.contains('has--children');
                    const $subnav = $parentLi ? $parentLi.querySelector('[data-nav="sub-nav"]') : null;

                    if ($subnav && hasChildren) {
                        if ($subnav.classList.contains('is--active')) {
                            this.closeSubNav($subnav, 'height');
                            $menuItem.setAttribute('aria-expanded', 'false');
                        } else {
                            this.openSubNav($subnav, $menuItem);
                            $menuItem.setAttribute('aria-expanded', 'true');
                        }
                        e.preventDefault();
                    }
                });

                // keyboard support
                $menuItem.addEventListener('keydown', (e) => {
                    const key = e.key;
                    const $allTopItems = Array.from(this.$root.querySelectorAll('[data-nav="item"]'));
                    const idx = $allTopItems.indexOf($menuItem);
                    const $parentLi = $menuItem.closest('li');
                    const $subnav = $parentLi ? $parentLi.querySelector('[data-nav="sub-nav"]') : null;
                    const hasChildren = $parentLi && $parentLi.classList.contains('has--children');

                    if (key === 'ArrowRight') {
                        const next = $allTopItems[(idx + 1) % $allTopItems.length];
                        next && next.focus();
                        e.preventDefault();
                    } else if (key === 'ArrowLeft') {
                        const prev = $allTopItems[(idx - 1 + $allTopItems.length) % $allTopItems.length];
                        prev && prev.focus();
                        e.preventDefault();
                    } else if (key === 'ArrowDown') {
                        if (hasChildren && $subnav) {
                            this.openSubNav($subnav, $menuItem);
                            $menuItem.setAttribute('aria-expanded', 'true');
                            const firstItem = $subnav.querySelector('[role="menuitem"], a, button');
                            firstItem && firstItem.focus();
                            e.preventDefault();
                        }
                    } else if (key === 'Escape') {
                        if ($subnav && $subnav.classList.contains('is--active')) {
                            this.closeSubNav($subnav, 'height');
                            $menuItem.setAttribute('aria-expanded', 'false');
                            e.preventDefault();
                        }
                    } else if (key === 'Home') {
                        $allTopItems[0] && $allTopItems[0].focus();
                        e.preventDefault();
                    } else if (key === 'End') {
                        $allTopItems[$allTopItems.length - 1] && $allTopItems[$allTopItems.length - 1].focus();
                        e.preventDefault();
                    }
                });
            }
        }

        const $thirdMenuItems = this.$root.querySelectorAll('[data-nav="third-nav-trigger"]');
        if ($thirdMenuItems.length > 0) {
            for (let i = 0; i < $thirdMenuItems.length; i++) {
                const $thirdMenuItem = $thirdMenuItems[i];
                $thirdMenuItem.addEventListener('click', (e) => {
                    if (window.innerWidth <= 1023) {
                        const $thirdnav = $thirdMenuItem.parentElement.querySelector('[data-nav="third-nav"]');

                        if ($thirdnav) {
                            if ($thirdnav.classList.contains('is--active')) {
                                this.closeThirdNav($thirdnav, 'height');
                                $thirdMenuItem.setAttribute('aria-expanded', 'false');
                            } else {
                                this.openThirdNav($thirdnav, $thirdMenuItem);
                                $thirdMenuItem.setAttribute('aria-expanded', 'true');
                            }
                        }
                        e.preventDefault();
                    }
                });

                $thirdMenuItem.addEventListener('keydown', (e) => {
                    const key = e.key;
                    const $thirdnav = $thirdMenuItem.parentElement.querySelector('[data-nav="third-nav"]');
                    if (key === 'ArrowRight' || key === 'ArrowDown') {
                        if ($thirdnav) {
                            this.openThirdNav($thirdnav, $thirdMenuItem);
                            $thirdMenuItem.setAttribute('aria-expanded', 'true');
                            const first = $thirdnav.querySelector('[role="menuitem"], a');
                            first && first.focus();
                            e.preventDefault();
                        }
                    } else if (key === 'Escape') {
                        if ($thirdnav && $thirdnav.classList.contains('is--active')) {
                            this.closeThirdNav($thirdnav, 'height');
                            $thirdMenuItem.setAttribute('aria-expanded', 'false');
                            e.preventDefault();
                        }
                    }
                });
            }
        }

        const $subnavCloseButtons = this.$root.querySelectorAll('[data-nav="sub-nav-back"]');
        if ($subnavCloseButtons.length > 0) {
            for (let i = 0; i < $subnavCloseButtons.length; i++) {
                const $subnavCloseButton = $subnavCloseButtons[i];

                $subnavCloseButton.addEventListener('click', (e) => {
                    const $subnav = $subnavCloseButton.closest('[data-nav="sub-nav"]');
                    this.closeSubNav($subnav, 'height');
                    // update controlling trigger aria-expanded
                    const $trigger = this.$root.querySelector('[data-nav="item"][aria-controls="' + $subnav.id + '"]');
                    if ($trigger) $trigger.setAttribute('aria-expanded', 'false');
                });
            }
        }

        const $thirdnavCloseButtons = this.$root.querySelectorAll('[data-nav="third-nav-back"]');
        if ($thirdnavCloseButtons.length > 0) {
            for (let i = 0; i < $thirdnavCloseButtons.length; i++) {
                const $thirdnavCloseButton = $thirdnavCloseButtons[i];

                $thirdnavCloseButton.addEventListener('click', (e) => {
                    const $thirdnav = $thirdnavCloseButton.closest('[data-nav="third-nav"]');
                    this.closeSubNav($thirdnav, 'height');
                    const $trigger = this.$root.querySelector('[aria-controls="' + $thirdnav.id + '"]');
                    if ($trigger) $trigger.setAttribute('aria-expanded', 'false');
                });
            }
        }

        const $body = document.querySelector('body');
        $body.addEventListener('click', (e) => {
            if (window.innerWidth > 1023) {
                const $siteheader = document.querySelector('[data-nav="header"]');
                if ($siteheader.classList.contains('has--sub-nav-open')) {
                    const $clickedElement = e.target;
                    const checkParentSubnav = $clickedElement.closest('[data-nav="sub-nav"]');
                    if (!checkParentSubnav && !$clickedElement.hasAttribute('data-nav')) {
                        const $subnav = document.querySelector('[data-nav="sub-nav"].is--active');
                        if ($subnav) {
                            this.closeSubNav($subnav, 'height');
                            const $trigger = this.$root.querySelector('[data-nav="item"][aria-controls="' + $subnav.id + '"]');
                            if ($trigger) $trigger.setAttribute('aria-expanded', 'false');
                        }
                    }
                }
            }
        });

        // Aquarium sizes toggle (mobile <= 1023px)
        const sizeToggles = this.$root.querySelectorAll('[data-nav="size-toggle"]');
        if (sizeToggles.length > 0) {
            sizeToggles.forEach(($btn) => {
                $btn.addEventListener('click', (e) => {
                    if (window.innerWidth > 1023) return; // only mobile/tablet
                    const controlsId = $btn.getAttribute('aria-controls');
                    if (!controlsId) return;
                    const $panel = document.getElementById(controlsId);
                    if (!$panel) return;
                    const isExpanded = $btn.getAttribute('aria-expanded') === 'true';
                    $btn.setAttribute('aria-expanded', String(!isExpanded));
                    $panel.setAttribute('aria-hidden', String(isExpanded));
                });

                // Close panel on Escape when focused within panel
                $btn.addEventListener('keydown', (e) => {
                    if (e.key === 'Escape') {
                        const controlsId = $btn.getAttribute('aria-controls');
                        const $panel = controlsId ? document.getElementById(controlsId) : null;
                        if ($panel && $btn.getAttribute('aria-expanded') === 'true') {
                            $btn.setAttribute('aria-expanded', 'false');
                            $panel.setAttribute('aria-hidden', 'true');
                            $btn.focus();
                        }
                    }
                });
            });
        }
    }

    /**
     * Enable a basic focus trap within the navigation when the burger (mobile menu) is open.
     * @returns {void}
     */
    enableFocusTrap () {
        const container = this.$root;
        const getFocusable = () => Array.from(container.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )).filter(el => el.offsetParent !== null);

        this._focusTrapHandler = (e) => {
            if (!this.$root.classList.contains('has--mobile-menu-visible')) return;
            if (e.key !== 'Tab') return;
            const focusables = getFocusable();
            if (!focusables.length) return;
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            if (e.shiftKey) {
                if (document.activeElement === first) {
                    last.focus();
                    e.preventDefault();
                }
            } else {
                if (document.activeElement === last) {
                    first.focus();
                    e.preventDefault();
                }
            }
        };
        document.addEventListener('keydown', this._focusTrapHandler);
    }

    /**
     * Disable the focus trap if enabled.
     * @returns {void}
     */
    disableFocusTrap () {
        if (this._focusTrapHandler) {
            document.removeEventListener('keydown', this._focusTrapHandler);
            this._focusTrapHandler = null;
        }
    }

    /**
     * Open a sub navigation panel.
     * @param {HTMLElement} subnav The sub navigation element.
     * @param {HTMLElement} [trigger] The trigger element controlling this panel.
     * @returns {void}
     */
    openSubNav (subnav, trigger) {
        const $subnav = subnav;
        const $siteheader = document.querySelector('[data-nav="header"]');
        const windowWidth = window.innerWidth;

        if (windowWidth > 1023) {
            const $activeSubnav = document.querySelector('[data-nav="sub-nav"].is--active');
            const $subnavInner = $subnav.querySelector('[data-nav="sub-nav-inner"]');
            const subnavHeight = $subnavInner.offsetHeight;
            $subnav.style.maxHeight = subnavHeight + 'px';
            setTimeout(() => {
                if ($activeSubnav) {
                    this.closeSubNav($activeSubnav, 'opacity');
                    const $activeTrigger = this.$root.querySelector('[data-nav="item"][aria-controls="' + $activeSubnav.id + '"]');
                    if ($activeTrigger) $activeTrigger.setAttribute('aria-expanded', 'false');
                }
            }, 200);
            setTimeout(() => {
                $subnav.style.maxHeight = 'none';
            }, 500);
        } else {
            $subnav.classList.add('is--hiding');
        }
        $siteheader.classList.add('has--sub-nav-open');
        $subnav.classList.add('is--active');
        $subnav.setAttribute('aria-hidden', 'false');
        if (trigger) trigger.setAttribute('aria-expanded', 'true');
    }

    /**
     * Close a sub navigation panel.
     * @param {HTMLElement} subnav The sub navigation element.
     * @param {('opacity'|'height')} [animation] Optional animation mode for desktop.
     * @returns {void}
     */
    closeSubNav (subnav, animation) {
        const $subnav = subnav;
        $subnav.classList.remove('is--active');
        const $siteheader = document.querySelector('[data-nav="header"]');
        $siteheader.classList.remove('has--sub-nav-open');
        $subnav.setAttribute('aria-hidden', 'true');
        const windowWidth = window.innerWidth;
        if (windowWidth > 1023) {
            if (animation) {
                if (animation === 'opacity') {
                    $subnav.classList.add('has--animation-opacity');

                    setTimeout(() => {
                        $subnav.classList.remove('has--animation-opacity');
                        $subnav.style.maxHeight = '0';
                    }, 250);
                } else {
                    const $subnavInner = $subnav.querySelector('[data-nav="sub-nav-inner"]');
                    const subnavHeight = $subnavInner.offsetHeight;
                    $subnav.style.maxHeight = subnavHeight + 'px';
                    setTimeout(() => {
                        $subnav.style.maxHeight = '0';
                    }, 100);
                }
            }
        } else {
            setTimeout(() => {
                $subnav.classList.remove('is--hiding');
            }, 500);
        }
    }

    /**
     * Open a third level navigation panel.
     * @param {HTMLElement} thirdnav The third level nav element.
     * @param {HTMLElement} [trigger] The trigger element controlling this panel.
     * @returns {void}
     */
    openThirdNav (thirdnav, trigger) {
        const $thirdnav = thirdnav;
        const $siteheader = document.querySelector('[data-nav="header"]');
        const windowWidth = window.innerWidth;

        if (windowWidth > 1023) {
            const $activeThirdnav = document.querySelector('[data-nav="third-nav"].is--active');
            const $thirdnavInner = $thirdnav.querySelector('[data-nav="third-nav-inner"]');
            const thirdnavHeight = $thirdnavInner.offsetHeight;
            $thirdnav.style.maxHeight = thirdnavHeight + 'px';
            setTimeout(() => {
                if ($activeThirdnav) {
                    this.closeSubNav($activeThirdnav, 'opacity');
                }
            }, 200);
            setTimeout(() => {
                $thirdnav.style.maxHeight = 'none';
            }, 500);
        } else {
            $thirdnav.classList.add('is--hiding');
        }
        $siteheader.classList.add('has--third-nav-open');
        $thirdnav.classList.add('is--active');
        $thirdnav.setAttribute('aria-hidden', 'false');
        if (trigger) trigger.setAttribute('aria-expanded', 'true');
    }

    /**
     * Close a third level navigation panel.
     * @param {HTMLElement} thirdnav The third level nav element.
     * @param {('opacity'|'height')} [animation] Optional animation mode for desktop.
     * @returns {void}
     */
    closeThirdNav (thirdnav, animation) {
        const $thirdnav = thirdnav;
        $thirdnav.classList.remove('is--active');
        const $siteheader = document.querySelector('[data-nav="header"]');
        $siteheader.classList.remove('has--third-nav-open');
        $thirdnav.setAttribute('aria-hidden', 'true');
        const windowWidth = window.innerWidth;
        if (windowWidth > 1023) {
            if (animation) {
                if (animation === 'opacity') {
                    $thirdnav.classList.add('has--animation-opacity');

                    setTimeout(() => {
                        $thirdnav.classList.remove('has--animation-opacity');
                        $thirdnav.style.maxHeight = '0';
                    }, 250);
                } else {
                    const $thirdnavInner = $thirdnav.querySelector('[data-nav="third-nav-inner"]');
                    const thirdnavHeight = $thirdnavInner.offsetHeight;
                    $thirdnav.style.maxHeight = thirdnavHeight + 'px';
                    setTimeout(() => {
                        $thirdnav.style.maxHeight = '0';
                    }, 100);
                }
            }
        } else {
            setTimeout(() => {
                $thirdnav.classList.remove('is--hiding');
            }, 500);
        }
    }
}

export { Navigation };

window.addEventListener('content.loaded', (e) => {
    const { $context } = e.detail ?? {};
    if (!$context) return;
    const $navigation = $context.querySelector('[data-nav="header"]');
    if ($navigation) {
        const navigationAPI = new Navigation($navigation);
        // Historic behavior: expose API on DOM node
        // @ts-ignore
        $navigation.API = navigationAPI;
    }
});
